import { toPng } from 'html-to-image';
import jsPDF from 'jspdf';
import cac_logo from '@/assets/images/cac_logo_part_of_TBG_color.png';
import { nextTick } from 'vue';

type ImageInfo = {
  image: HTMLImageElement;
  width: number;
  height: number;
};

export async function getChartImages(elementIds: string[]): Promise<Array<ImageInfo>> {
  const images: ImageInfo[] = [];

  const desiredWidth = 1600;
  const pixelRatio = 2;

  // This is a fix for safari, grabbing the parent element so that it is loaded for when we actually grab it
  await document.fonts.ready;
  await nextTick();

  // Iterate over each element ID and capture the image
  for (let i = 0; i < elementIds.length; i++) {
    const elem = document.getElementById(elementIds[i]);

    // hide elements with class 'export-hide' before capturing the image
    const hidden = elem?.getElementsByClassName('export-hide');
    const originalStyles: { element: Element, display: string }[] = [];
    if (hidden) {
      Array.from(hidden).forEach((element) => {
        originalStyles.push({ element, display: (element as HTMLElement).style.display });
        (element as HTMLElement).style.setProperty('display', 'none', 'important');
      });
    }

    // Handle elements that should not wrap during export
    const noWrapElements = elem?.getElementsByClassName('export-no-wrap');
    const originalWhiteSpaceStyles: { element: HTMLElement, whiteSpace: string }[] = [];

    if (noWrapElements) {
      Array.from(noWrapElements).forEach((el) => {
        const element = el as HTMLElement;
        originalWhiteSpaceStyles.push({ element, whiteSpace: element.style.whiteSpace });
        element.style.setProperty('white-space', 'nowrap', 'important');
      });
    }


    if (!elem) {
      console.warn(`Element with ID '${elementIds[i]}' not found`);
      continue;
    }

    const elemWidth = elem.offsetWidth;
    const elemHeight = elem.offsetHeight;
    const scale = desiredWidth / elemWidth;
    const desiredHeight = elemHeight * scale;

    // Fix for safari not loading first image the first time this is exported to PDF
    const isSafariOrChrome = /safari|chrome/i.test(navigator.userAgent) && !/android/i.test(navigator.userAgent);
    let dataUrl = '';
    let attempt = 0;
    let maxAttempts = 1;
    if (isSafariOrChrome && i === 0) {
      console.info('Safari or Chrome detected, attempting to capture first image multiple times');
      maxAttempts = 3;
    } else {
      maxAttempts = 1;
    }
    const cycle: number[] = [];
    let repeat = true;

    while (repeat && attempt < maxAttempts) {
      dataUrl = await toPng(elem, {
        backgroundColor: 'transparent',
        width: desiredWidth,
        height: desiredHeight,
        style: {
          transform: `scale(${scale})`,
          transformOrigin: 'top left',
          width: `${elemWidth}px`,
          height: `${elemHeight}px`,
        },
        pixelRatio: pixelRatio,
        skipFonts: true,
      });

      attempt += 1;
      cycle[attempt] = dataUrl.length;
      if (dataUrl.length > cycle[attempt - 1]) repeat = false;
    }

    const image = await loadImage(dataUrl);
    if (!image) continue;
    images.push({
      image: image,
      width: image.width,
      height: image.height,
    });

    // Restore the original styles of elements with class 'export-hide'
    originalStyles.forEach(({ element, display }) => {
      (element as HTMLElement).style.setProperty('display', display || '', '');
    });

    // Restore the original white-space styles
    originalWhiteSpaceStyles.forEach(({ element, whiteSpace }) => {
      element.style.whiteSpace = whiteSpace;
    });
  }
  return images;
}

export async function getHeaderImage(maxWidth: number, name: string): Promise<HTMLImageElement> {
  const headerWidth = maxWidth;
  const headerHeight = 200;
  await document.fonts.ready;

  // Create a new canvas element to be used for drawing the header
  const headerCanvas = document.createElement('canvas');
  headerCanvas.width = headerWidth;
  headerCanvas.height = headerHeight;
  const ctx: CanvasRenderingContext2D | null = headerCanvas.getContext('2d');

  if (!ctx) {
    throw new Error('Failed to get the canvas 2D context');
  }

  ctx.fillStyle = 'white';
  ctx.fillRect(0, 0, headerWidth, headerHeight);

  const logo = await loadImage(cac_logo);
  const logoWidth = 345;
  const logoHeight = logo.height * (logoWidth / logo.width);

  const logoX = 40; // Horizontal position offset from the left
  const logoY = (headerHeight - logoHeight) / 2; // Vertically center

  // Draw the logo onto the canvas
  ctx.drawImage(logo, logoX, logoY, logoWidth, logoHeight);

  // text styling for the name and date
  ctx.fillStyle = '#415363'; // Dark Slate
  ctx.font = 'bold 36px Roboto';
  ctx.textBaseline = 'middle';

  const nameAndDate = `${name} - ${new Date().toLocaleDateString()}`;
  // Measure text width to calculate position
  const textX = logoX + logoWidth + 60;
  const textY = headerHeight / 2; // Vertically center the text

  // Draw the name and date text
  ctx.fillText(nameAndDate, textX, textY);

  const uri = headerCanvas.toDataURL('image/png');
  return await loadImage(uri);
}

export async function getPNG(images: ImageInfo[], header: HTMLImageElement): Promise<string> {
  // Create a new canvas element to handle drawing all the chart elements
  const canvas = document.createElement('canvas');
  const ctx: CanvasRenderingContext2D | null = canvas.getContext('2d');

  if (!ctx) {
    throw new Error('Failed to get the canvas 2D context');
  }

  const bottomPadding = 20;
  // Adjust total canvas height for adjusted header and bottom padding
  canvas.height = images.reduce((acc, { height }) => acc + height, 0) + header.height + bottomPadding;
  // Adjust total canvas width to accommodate the widest image
  const maxImageWidth = Math.max(header.width, ...images.map(({ width }) => width));
  canvas.width = maxImageWidth;
  ctx.fillStyle = 'white';
  ctx.fillRect(0, 0, canvas.width, canvas.height);

  // Center the header
  ctx.drawImage(header, (canvas.width - header.width) / 2, 0, header.width, header.height);

  // Draw each image centered on the canvas
  let currentHeight = header.height;
  images.forEach(({ image, width, height }) => {
    const x = (canvas.width - width) / 2;
    ctx.drawImage(image, x, currentHeight, width, height);
    currentHeight += height;
  });
  return canvas.toDataURL('image/png');
}

export async function exportToPNG(png: string, fileName?: string): Promise<void> {
  downloadURI(png, fileName || 'charts.png');
}

export async function exportToPDF(images: ImageInfo[], header: HTMLImageElement, fileName?: string): Promise<void> {
  const pdf = new jsPDF({ orientation: 'portrait', unit: 'pt', format: 'a4', });

  const pdfWidth = pdf.internal.pageSize.getWidth();
  const pdfHeight = pdf.internal.pageSize.getHeight();

  const imageMargin = 20;

  // Function to draw the header on the current page
  const drawHeader = (currentPagePosition: number) => {
    if (header && header.src) {
      const headerScale = Math.min(1, pdfWidth / header.width);
      const scaledHeaderWidth = header.width * headerScale;
      const scaledHeaderHeight = header.height * headerScale;
      const x = (pdfWidth - scaledHeaderWidth) / 2;
      pdf.addImage(header.src, 'PNG', x, currentPagePosition, scaledHeaderWidth, scaledHeaderHeight);
      return scaledHeaderHeight; // Return the height occupied by the header
    }
    return 0; // Return zero if no header was drawn
  };

  // Initially draw the header on the first page
  let currentPosition = drawHeader(0);
  currentPosition += imageMargin;

  for (const { image, width, height } of images) {
    if (!image || !image.src) {
      console.warn("Image is undefined or missing 'src'");
      continue;
    }
    const scale = Math.min(1, (pdfWidth - 2 * imageMargin) / width);
    const scaledWidth = width * scale;
    const scaledHeight = height * scale;

    // Check if the image fits on the current page
    if (currentPosition + scaledHeight + imageMargin > pdfHeight) {
      pdf.addPage();
      currentPosition = drawHeader(0);
      currentPosition += imageMargin;
    }

    const x = (pdfWidth - scaledWidth) / 2;
    pdf.addImage(image.src, 'PNG', x, currentPosition, scaledWidth, scaledHeight, undefined, 'SLOW');
    currentPosition += scaledHeight + imageMargin;
  }
  pdf.save(fileName || 'charts.pdf');
}

function loadImage(uri: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      resolve(img);
    };
    img.onerror = (error) => {
      console.error(`Failed to load image: ${uri}`, error);
      reject(error);
    };
    img.src = uri;
  });
}

function downloadURI(uri: string, name: string): void {
  const link = document.createElement('a');
  link.download = name;
  link.href = uri;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}
